import React, { useEffect } from "react";
import { get } from "../../utility/fetch";

// Mock function for fetching data
// In a real application, this would be your actual API call
const mockGet = async (url) => {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500));
  console.log(`Mocking GET request to: ${url}`);
  // Return a hardcoded patient object for demonstration
  return {
    id: "mock-patient-123",
    firstName: "John",
    lastName: "Doe",
    dateOfBirth: "1990-05-15", // Example DOB for age calculation
    gender: "Male",
    // ... other patient details
  };
};

// Mock function for calculating age
// In a real application, this would be your actual utility function
const mockCalculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "N/A";
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const VitalsRecords = ({ vitals = [], appointmentId }) => {
  const [patient, setPatient] = React.useState(null);
  const [vitalsData, setVitalsData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const getVitalsData = async () => {
    if (!appointmentId) return;

    setLoading(true);
    try {
      const response = await get(
        "patients/vital-by-appointmentId?appointmentId=1589&pageIndex=1&pageSize=1",
        // params: {
        //   appointmentId: appointmentId,
        //   pageIndex: 1,
        //   pageSize: 1,
        // },
        true
      );

      console.log("Vitals API response:", response);

      if (response && response.data) {
        setVitalsData(response.data);
      }
    } catch (e) {
      console.error("Error fetching vitals data:", e);
    } finally {
      setLoading(false);
    }
  };

  const getPatientDetails = async () => {
    try {
      // Using the mockGet function instead of the external 'get'
      const data = await mockGet("/patients/mock-patient-id/data");
      setPatient(data);
      console.log("Mock Patient Data:", data);
      console.log("Mock Patient ID:", data?.id);
    } catch (e) {
      console.error("Error fetching mock patient details:", e);
    }
  };

  useEffect(() => {
    getPatientDetails();
    getVitalsData();
  }, [appointmentId]);

  // Using vitalsData from API if available, otherwise fallback to passed vitals prop
  const displayVitals = vitalsData.length > 0 ? vitalsData : vitals;

  console.log("Vitals Records:", displayVitals);
  return (
    <>
      <style>
        {`
          /* Pure CSS for VitalsRecords Table */
          .vitals-sidebar {
            width: 100%;
            display: flex;
            justify-content: center;
            padding: 1rem;
            box-sizing: border-box; /* Include padding in element's total width and height */
            font-family: 'Inter', sans-serif; /* Ensure Inter font is used */
          }

          .vitals-container {
            background-color: #fff;
            border-radius: 0.5rem;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            width: 100%;
            max-width: 90rem; /* max-w-6xl */
            padding: 1.5rem;
            position: relative;
            overflow: hidden; /* For rounded corners */
          }

          .vitals-title {
            font-size: 1.25rem; /* text-xl */
            font-weight: 700; /* font-bold */
            margin-bottom: 1rem; /* mb-4 */
            color: #1f2937; /* Dark gray text */
          }

          .no-vitals-message {
            color: #6b7280; /* text-gray-500 */
            padding: 1rem; /* Add some padding for better spacing */
            text-align: center;
          }

          .vitals-cards-wrapper {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
            margin-top: 1rem;
          }

          .vitals-card {
            background-color: #ffffff;
            border: 2px solid #e5ffe5;
            border-radius: 0.75rem;
            padding: 1.5rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }

          .vitals-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 0.75rem;
            border-bottom: 2px solid #e5ffe5;
          }

          .vitals-card-header h3 {
            font-size: 1.125rem;
            font-weight: 600;
            color: #166534;
            margin: 0;
          }

          .visit-date {
            font-size: 0.875rem;
            color: #6b7280;
            font-weight: 500;
          }

          .vitals-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }

          .vital-item {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .vital-item.full-width {
            grid-column: 1 / -1;
          }

          .vital-item label {
            font-size: 0.75rem;
            font-weight: 600;
            color: #166534;
            text-transform: uppercase;
            letter-spacing: 0.025em;
          }

          .vital-item span {
            font-size: 0.875rem;
            color: #1f2937;
            font-weight: 500;
            padding: 0.5rem 0.75rem;
            background-color: #f9fafb;
            border-radius: 0.375rem;
            border: 1px solid #e5e7eb;
          }

          /* Responsive adjustments */
          @media (max-width: 768px) {
            .vitals-container {
              padding: 1rem;
            }
            .vitals-title {
              font-size: 1.125rem;
            }
            .vitals-table th, .vitals-table td {
              padding: 0.5rem 0.75rem;
              font-size: 0.875rem;
            }
          }

          @media (max-width: 480px) {
            .vitals-container {
              padding: 0.75rem;
            }
            .vitals-title {
              font-size: 1rem;
            }
            .vitals-table {
              min-width: 600px; /* Adjust min-width for very small screens */
            }
          }
        `}
      </style>

      <aside className="vitals-sidebar">
        <div className="vitals-container">
          <h2 className="vitals-title">Vitals Records</h2>
          {loading ? (
            <p className="no-vitals-message">Loading vitals...</p>
          ) : displayVitals.length === 0 ? (
            <p className="no-vitals-message">No vitals recorded.</p>
          ) : (
            <div className="vitals-cards-wrapper">
              {displayVitals.map((v, i) => (
                <div key={i} className="vitals-card">
                  <div className="vitals-card-header">
                    {/* <h3>Visit #{i + 1}</h3> */}
                    <span className="visit-date">{v.dateOfVisit || "N/A"}</span>
                  </div>

                  <div className="vitals-grid">
                    <div className="vital-item">
                      <label>Patient</label>
                      <span>
                        {v?.patientName} 
                      </span>
                    </div>

                    {/* <div className="vital-item">
                      <label>Age</label>
                      <span>
                        {patient
                          ? mockCalculateAge(patient.dateOfBirth)
                          : "N/A"}
                      </span>
                    </div> */}

                    {/* <div className="vital-item">
                      <label>Sex</label>
                      <span>{patient?.gender || "N/A"}</span>
                    </div> */}

                    <div className="vital-item">
                      <label>Nurse</label>
                      <span>{v.vitalNurseName || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>Temperature (°C)</label>
                      <span>{v.temperature || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>Blood Pressure</label>
                      <span>{v.bloodPressure || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>Heart Rate</label>
                      <span>{v.heartPulse || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>Respiratory</label>
                      <span>{v.respiratory || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>Blood Sugar</label>
                      <span>{v.bloodSugar || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>O₂ Saturation</label>
                      <span>{v.oxygenSaturation || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>Height (cm)</label>
                      <span>{v.height || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>Weight (kg)</label>
                      <span>{v.weight || "N/A"}</span>
                    </div>

                    <div className="vital-item">
                      <label>BMI</label>
                      <span>{v.bmi || "N/A"}</span>
                    </div>

                    <div className="vital-item full-width">
                      <label>Notes</label>
                      <span>{v.notes?.join(", ") || "No notes"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default VitalsRecords;
